import { useCallback, useReducer, useEffect, useRef } from "react"
import { calculateMinWidth } from "src/utils/table-render-functions"
import dayjs from "dayjs"
import { DATE_FORMAT } from "src/utils/constants"

const EMPTY_ULTIMO_EVENTO = {
    data_referencia: "",
    evento_credito: "",
    score_evento: "",
}

const initialState = {
    loading: false,
    rows: [],
    ultimoEvento: EMPTY_ULTIMO_EVENTO,
    alert: { open: false, message: "", type: "" },
}

function reducer(state, action) {
    switch (action.type) {
        case "SET_LOADING":
            return { ...state, loading: action.payload }
        case "SET_ROWS":
            return { ...state, rows: action.payload }
        case "SET_ULTIMO_EVENTO":
            return { ...state, ultimoEvento: action.payload }
        case "SET_ALERT":
            return { ...state, alert: { ...state.alert, ...action.payload } }
        case "CLOSE_ALERT":
            return { ...state, alert: { ...state.alert, open: false } }
        default:
            return state
    }
}

const ordenaPorDataReferencia = (arr, order = "asc") =>
    [...arr].sort((a, b) => {
        const dateA = a?.DataReferencia
            ? new Date(a.DataReferencia)
            : new Date(0)

        const dateB = b?.DataReferencia
            ? new Date(b.DataReferencia)
            : new Date(0)

        return order === "desc" ? dateB - dateA : dateA - dateB
    })

const getResponseResults = (response) => {
    const body = response?.body

    if (Array.isArray(body)) return body
    if (Array.isArray(body?.results)) return body.results
    if (Array.isArray(body?.data)) return body.data
    if (Array.isArray(body?.data?.results)) return body.data.results

    return []
}

const buildUltimoEventoFromRow = (row = {}) => ({
    data_referencia: row?.DataReferencia ?? "",
    evento_credito: row?.EventoCreditoDevedor ?? "",
    score_evento: row?.ScoreEvento ?? "",
})

const removeRowIdentifiers = (row = {}) => {
    const payload = { ...row }

    delete payload.id
    delete payload.ID

    return payload
}

export const useUltimoEventoCreditoDevedor = (
    emissaoService,
    cpfCnpj,
    dadosEmpresa,
    cetip
) => {
    const [state, dispatch] = useReducer(reducer, initialState)

    const loadedRef = useRef(cpfCnpj)

    useEffect(() => {
        if (loadedRef.current === cpfCnpj) return

        loadedRef.current = cpfCnpj

        dispatch({ type: "SET_ROWS", payload: [] })
        dispatch({ type: "SET_ULTIMO_EVENTO", payload: EMPTY_ULTIMO_EVENTO })
        dispatch({ type: "CLOSE_ALERT" })
    }, [cpfCnpj])

    const normalizeRows = useCallback((listaResultados = []) => {
        return ordenaPorDataReferencia(listaResultados, "desc").map(
            (obj, index) => ({
                ...obj,
                id: obj?.id ?? obj?.ID ?? index,
            })
        )
    }, [])

    const applyRows = useCallback(
        (listaResultados = []) => {
            const rows = normalizeRows(listaResultados)
            const primeiraLinha = rows[0] || {}

            dispatch({ type: "SET_ROWS", payload: rows })
            dispatch({
                type: "SET_ULTIMO_EVENTO",
                payload: buildUltimoEventoFromRow(primeiraLinha),
            })

            return rows
        },
        [normalizeRows]
    )

    const loadTableRows = useCallback(async () => {
        if (!cpfCnpj) {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "CPFCNPJ não informado.",
                    type: "warning",
                },
            })

            dispatch({ type: "SET_ROWS", payload: [] })
            dispatch({ type: "SET_ULTIMO_EVENTO", payload: EMPTY_ULTIMO_EVENTO })

            return
        }

        dispatch({ type: "SET_LOADING", payload: true })

        try {
            const response = await emissaoService.eventoCreditoDevedor(
                "get",
                `?CPFCNPJ=${encodeURIComponent(cpfCnpj.toUpperCase())}`
            )

            const listaResultados = getResponseResults(response)

            if (!listaResultados.length) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Não há dados para o CPF/CNPJ consultado.",
                        type: "warning",
                    },
                })

                dispatch({ type: "SET_ROWS", payload: [] })
                dispatch({
                    type: "SET_ULTIMO_EVENTO",
                    payload: EMPTY_ULTIMO_EVENTO,
                })

                return
            }

            dispatch({ type: "CLOSE_ALERT" })
            applyRows(listaResultados)
        } catch {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "Erro ao buscar dados ou CPF/CNPJ não encontrado.",
                    type: "warning",
                },
            })

            dispatch({ type: "SET_ROWS", payload: [] })
            dispatch({ type: "SET_ULTIMO_EVENTO", payload: EMPTY_ULTIMO_EVENTO })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [cpfCnpj, emissaoService, applyRows])

    const addRow = useCallback(async () => {
        const lastItem = state.rows[0]

        const payload = lastItem
            ? removeRowIdentifiers(lastItem)
            : {
                  DataReferencia: dayjs().format(DATE_FORMAT),
                  DataValidade: dayjs().add(1, "year").format(DATE_FORMAT),
                  CETIP: dadosEmpresa?.CETIP || cetip,
                  CPFCNPJ: cpfCnpj,
                  Devedor:
                      dadosEmpresa?.Devedor ||
                      dadosEmpresa?.RazaoSocial ||
                      "N/A",
                  EventoCreditoDevedor: "N/A",
                  ScoreEvento: 0,
              }

        dispatch({ type: "SET_LOADING", payload: true })

        try {
            await emissaoService.eventoCreditoDevedor("post", payload)
            await loadTableRows()
        } catch {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "Erro ao adicionar registro.",
                    type: "error",
                },
            })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [
        state.rows,
        cetip,
        cpfCnpj,
        dadosEmpresa,
        emissaoService,
        loadTableRows,
    ])

    const deleteRow = useCallback(
        async (row) => {
            const id = row?.ID ?? row?.id

            if (!id) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Registro sem ID para exclusão.",
                        type: "warning",
                    },
                })
                return
            }

            dispatch({ type: "SET_LOADING", payload: true })

            try {
                await emissaoService.eventoCreditoDevedor("delete", id)
                await loadTableRows()
            } catch {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Erro ao deletar registro.",
                        type: "error",
                    },
                })
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
        },
        [emissaoService, loadTableRows]
    )

    const updateRow = useCallback(
        async (row) => {
            const id = row?.ID ?? row?.id

            if (!id) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Registro sem ID para atualização.",
                        type: "warning",
                    },
                })

                return row
            }

            dispatch({ type: "SET_LOADING", payload: true })

            try {
                await emissaoService.eventoCreditoDevedor("patch", id, row)
                await loadTableRows()

                return row
            } catch (error) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Erro ao atualizar registro.",
                        type: "error",
                    },
                })

                throw error
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
        },
        [emissaoService, loadTableRows]
    )

    const setAlert = useCallback(
        (payload) => dispatch({ type: "SET_ALERT", payload }),
        []
    )

    const closeAlert = useCallback(() => dispatch({ type: "CLOSE_ALERT" }), [])

    return {
        state: {
            ...state,
            minWidthEvento:
                calculateMinWidth(state.rows, "EventoCreditoDevedor") / 2,
        },
        actions: {
            loadTableRows,
            deleteRow,
            addRow,
            updateRow,
            setAlert,
            closeAlert,
            setRows: (rows) => applyRows(rows),
        },
    }
}